/**
 * Delete the old app's storage from this origin, before any third party runs.
 *
 * `https://lifekees.com` was the password manager's origin until the split;
 * returning visitors' browsers still hold its IndexedDB (encrypted vaults,
 * rollback floors, the outbox, and possibly a Secret Key remembered in the
 * clear), its Web Storage (a bearer token, passkey hints, the 2016 apps'
 * effectively-plaintext legacy keys) and its service worker with a cached
 * shell. This origin now runs third-party code, which could read all of it.
 * `inventory.ts` lists every name, with its sources.
 *
 * This is the third of four layers, and the one this page can be sure of:
 *
 *  1. the kill-switch service worker at `/sw.js` (`site/public/sw.js`);
 *  2. `Clear-Site-Data` on the responses nginx sends (browser support varies,
 *     see docs/SECURITY.md §11 "The old origin");
 *  3. **this**: evaluated at module load, on every marketing page, by the
 *     first import of each page's entry, so the promise exists before any
 *     other module of the page has run;
 *  4. `third-party.ts`, the only code that creates a `<script>` element,
 *     awaits {@link oldOriginWiped} and refuses unless it is `complete`.
 *
 * It runs on every load rather than once behind a marker: it is cheap and
 * idempotent, and an old-app tab still open somewhere can write a key back
 * after one pass.
 *
 * Within one load it makes TWO passes over Web Storage, Cache Storage and the
 * worker registrations: one at once, and one after the IndexedDB delete has
 * settled, and the report is judged on the second. A delete blocked by an old
 * tab can wait for minutes; that tab is alive the whole time and can write
 * `lk.lastEmail`, `lk.passkeyCred.<email>` (a 2016 page: `email`,
 * `bio_<email>`) after the first pass. The second pass is what is still true
 * when `third-party.ts` acts on the report — once the tab has let go, which it
 * does by closing, it cannot write again.
 *
 * Losing an unsynced outbox here is accepted and documented: the old origin's
 * data is deleted, not migrated (the vault origin starts fresh).
 *
 * What counts as `complete`: every step either deleted what it found, found
 * nothing, or was refused access to the store outright (a `SecurityError` —
 * storage blocked for this origin, as in some private modes). A store this
 * page cannot open is equally closed to any other script in this page. A
 * database delete that is *blocked* — an old-app tab holding a connection open
 * — is never complete: the notice asks the user to close it, and the wait
 * continues until the browser lets the delete through.
 */
import { OLD_IDB_NAMES, isOldCacheName, isOldStorageKey } from './inventory.js';

export type StepOutcome =
  /** Something was there, and it is gone. */
  | 'deleted'
  /** Nothing to delete (or the API does not exist in this browser). */
  | 'absent'
  /** The browser refused access to the store (`SecurityError`). */
  | 'denied'
  /** It failed, or did not finish. Not complete. */
  | 'failed';

export interface WipeReport {
  complete: boolean;
  steps: {
    localStorage: StepOutcome;
    sessionStorage: StepOutcome;
    indexedDB: StepOutcome;
    caches: StepOutcome;
    serviceWorkers: StepOutcome;
  };
}

/** The browser surfaces the wipe touches. Injectable for the tests. */
export interface WipeEnv {
  /** Each accessor may throw (a blocked store throws on access, not on use). */
  localStorage: () => Storage | null | undefined;
  sessionStorage: () => Storage | null | undefined;
  indexedDB: () => IDBFactory | null | undefined;
  caches: () => CacheStorage | null | undefined;
  serviceWorker: () => Pick<ServiceWorkerContainer, 'getRegistrations'> | null | undefined;
  /** Called once if a database delete is blocked by another open connection. */
  onBlocked: () => void;
}

const COMPLETE: ReadonlySet<StepOutcome> = new Set(['deleted', 'absent', 'denied']);

function isSecurityError(error: unknown): boolean {
  return (error as { name?: unknown } | null)?.name === 'SecurityError';
}

function refused(error: unknown): StepOutcome {
  return isSecurityError(error) ? 'denied' : 'failed';
}

function wipeWebStorage(get: () => Storage | null | undefined): StepOutcome {
  let store: Storage | null | undefined;
  try {
    store = get();
  } catch (error) {
    return refused(error);
  }
  if (!store) return 'absent';
  try {
    // Snapshot first: removing while walking `key(i)` skips entries.
    const keys: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key !== null) keys.push(key);
    }
    const doomed = keys.filter(isOldStorageKey);
    for (const key of doomed) store.removeItem(key);
    return doomed.length > 0 ? 'deleted' : 'absent';
  } catch (error) {
    return refused(error);
  }
}

function deleteDatabase(factory: IDBFactory, name: string, onBlocked: () => void): Promise<StepOutcome> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.deleteDatabase(name);
    } catch (error) {
      resolve(refused(error));
      return;
    }
    // `success` fires once the database is gone — immediately, or after a
    // `blocked` once the other connections close. There is no timeout on
    // purpose: a database still held open by an old-app tab is not deleted,
    // and nothing third-party may load until it is.
    request.onsuccess = () => resolve('deleted');
    request.onerror = () => resolve(refused(request.error));
    request.onblocked = () => onBlocked();
  });
}

async function wipeIndexedDB(env: WipeEnv): Promise<StepOutcome> {
  let factory: IDBFactory | null | undefined;
  try {
    factory = env.indexedDB();
  } catch (error) {
    return refused(error);
  }
  if (!factory) return 'absent';
  let blockedSaid = false;
  const onBlocked = () => {
    if (blockedSaid) return;
    blockedSaid = true;
    env.onBlocked();
  };
  const outcomes = await Promise.all(OLD_IDB_NAMES.map((name) => deleteDatabase(factory, name, onBlocked)));
  return outcomes.every((o) => COMPLETE.has(o)) ? 'deleted' : (outcomes.find((o) => !COMPLETE.has(o)) ?? 'failed');
}

async function wipeCaches(env: WipeEnv): Promise<StepOutcome> {
  try {
    const storage = env.caches();
    if (!storage) return 'absent';
    const doomed = (await storage.keys()).filter(isOldCacheName);
    const results = await Promise.all(doomed.map((name) => storage.delete(name)));
    // `delete` answering false means it was already gone: still gone.
    void results;
    return doomed.length > 0 ? 'deleted' : 'absent';
  } catch (error) {
    return refused(error);
  }
}

async function unregisterWorkers(env: WipeEnv): Promise<StepOutcome> {
  try {
    const container = env.serviceWorker();
    if (!container) return 'absent';
    // This origin registers no worker of its own any more, so every
    // registration here is the old app's.
    const registrations = await container.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
    return registrations.length > 0 ? 'deleted' : 'absent';
  } catch (error) {
    return refused(error);
  }
}

/**
 * One step's outcome over the two passes. The final pass decides whether it is
 * complete (that is the state a third party would meet); "deleted" is kept if
 * either pass deleted something, so the report still says the old app had been
 * here (which is what `showMovedNotice` reads).
 */
function settle(first: StepOutcome, last: StepOutcome): StepOutcome {
  if (!COMPLETE.has(last)) return last;
  return first === 'deleted' || last === 'deleted' ? 'deleted' : last;
}

/** Run every step, then the cheap ones again once IndexedDB has settled. Never rejects. */
export async function wipeOldOrigin(env: WipeEnv): Promise<WipeReport> {
  const local = wipeWebStorage(env.localStorage);
  const session = wipeWebStorage(env.sessionStorage);
  const [indexedDB, caches, serviceWorkers] = await Promise.all([
    wipeIndexedDB(env).catch(refused),
    wipeCaches(env),
    unregisterWorkers(env),
  ]);
  // The second pass: whatever an old tab wrote while it held the delete up.
  const localAgain = wipeWebStorage(env.localStorage);
  const sessionAgain = wipeWebStorage(env.sessionStorage);
  const [cachesAgain, serviceWorkersAgain] = await Promise.all([wipeCaches(env), unregisterWorkers(env)]);
  const steps = {
    localStorage: settle(local, localAgain),
    sessionStorage: settle(session, sessionAgain),
    indexedDB,
    caches: settle(caches, cachesAgain),
    serviceWorkers: settle(serviceWorkers, serviceWorkersAgain),
  };
  return { complete: Object.values(steps).every((o) => COMPLETE.has(o)), steps };
}

/** Whether this load found the old app's storage here: a returning user. */
export function foundOldApp(report: WipeReport): boolean {
  return Object.values(report.steps).some((o) => o === 'deleted');
}

/** Show the page's "close the old app" notice, if it has one. */
function showBlockedNotice(): void {
  const notice = globalThis.document?.getElementById('old-origin-notice');
  if (notice) notice.hidden = false;
}

/**
 * Show the page's "Lifekees moved" notice, if it has one, when this load
 * deleted something: the visitor used the old app in this browser, and has
 * just lost its saved data here (by design). Without a word, the vault's
 * fresh-device sign-in that follows — email again, then a Secret Key prompt
 * they may not have seen in a year — reads like a breach or a bug.
 */
export function showMovedNotice(report: WipeReport, doc: Document | undefined = globalThis.document): void {
  if (!foundOldApp(report)) return;
  const notice = doc?.getElementById('old-origin-moved');
  if (notice) notice.hidden = false;
}

/** The real browser, read lazily so a blocked store throws inside a step. */
export function browserEnv(): WipeEnv {
  return {
    localStorage: () => globalThis.localStorage,
    sessionStorage: () => globalThis.sessionStorage,
    indexedDB: () => globalThis.indexedDB,
    caches: () => globalThis.caches,
    serviceWorker: () => globalThis.navigator?.serviceWorker,
    onBlocked: showBlockedNotice,
  };
}

/**
 * The wipe of this page load. Started when this module is evaluated, which is
 * the first thing each marketing page's entry does; `third-party.ts` awaits it.
 */
export const oldOriginWiped: Promise<WipeReport> = wipeOldOrigin(browserEnv());
void oldOriginWiped.then((report) => showMovedNotice(report));
